/**
 * LprSetup.tsx — v0.4.9.4.6 — NetworkView "AI Training" > LPR subtab.
 *
 * Lets an admin (superadmin or sadmin) tune the plate OCR rule set from the
 * PWA, without restarting the Node API or the Python detector subprocess.
 *
 * What lives here:
 *   - Patterns (regex per plate type: car / motorcycle / trailer / ...)
 *   - Country-code prefix list (what the detector strips before scoring)
 *   - Scoring weights (length penalties, belarusian bonus)
 *   - Digit-count guardrails
 *
 * What doesn't live here:
 *   - The Edit / False-Positive form (that's in ResultDisplay.tsx).
 *   - The actual rules file editing — that's a single PUT to /api/lpr/rules.
 *
 * Side panels:
 *   - "Reload" button — forces the Python detector to re-read the rules
 *     file on the next plate check (mtime bump).
 *   - "Download training CSV" — links to the CSV export endpoint.
 *   - "Get LLM prompt" — fetches the pre-formatted prompt and copies it
 *     to the clipboard for the admin to paste into the LLM-friend.
 *
 * Mount: the NetworkView page should add this component as a tab/route
 * (e.g. `<Route path="lpr" element={<LprSetup />} />`). The component
 * self-renders its own card layout.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Download, FileText, RefreshCw, Sparkles } from 'lucide-react';
import { api } from '../services/api';

type PatternMap = Record<string, string>;
type LengthPenalty = Record<'lt4' | 'lt5' | 'gt8' | 'gt12' | 'gt20', number>;
type Scoring = {
  min_length: number;
  max_length: number;
  belarusian_weight: number;
  length_penalties: LengthPenalty;
};

type Rules = {
  version: string;
  country_codes: string[];
  patterns: PatternMap;
  scoring: Scoring;
  min_digits: number;
  max_digits: number;
};

// v0.4.9.4.6: EMPTY_SCORING / EMPTY_RULES removed. Both were dead —
// EMPTY_SCORING was referenced only by EMPTY_RULES, and EMPTY_RULES by
// nothing. `load()` always gets the rules from GET /api/lpr/rules and
// `setRules(res.rules)`, so there is no code path that ever needed a local
// placeholder. Worse than noise: an unused "default" scoring block sitting in
// the file is a second, divergent copy of the values that plate_rules.json
// owns, and the two would drift.

function isValidRegex(s: string): string | null {
  if (typeof s !== 'string' || s.length === 0) return 'empty';
  try { new RegExp(s); return null; } catch (e: any) { return e.message; }
}

function isValidCode(cc: string): boolean {
  return /^[A-Z]{2,3}$/.test(cc);
}

export default function LprSetup() {
  const { t } = useTranslation();
  const [rules, setRules] = useState<Rules | null>(null);
  const [mtime, setMtime] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // v0.4.9.4.6: the org the CSV export and the LLM prompt are scoped to.
  //
  // The JWT payload is {userId, role, clientId, storeIds, apk_version_code}
  // — there is no organization claim, and X-Store-Id is a B2B store id, not
  // the civil_respondents.id that plate_reading_corrections.organization_id
  // stores. So there is nothing to default this from, and the server
  // refuses an SAdmin export that does not name an org.
  //
  // This is a deliberate placeholder, not the finished UX: it should become
  // a picker over the orgs the SAdmin actually owns, once that relationship
  // is verified against the real schema. See DEPLOYMENT.md, "Follow-ups".
  const [orgId, setOrgId] = useState<string>('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.lprGetRules();
      setRules(res.rules as Rules);
      setMtime(res.mtime);
      setDirty(false);
    } catch (err: any) {
      setError(err.message || t('lpr.loadFailed'));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // --- mutators that mark the form dirty ---

  function update<K extends keyof Rules>(key: K, value: Rules[K]) {
    setRules(prev => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  }

  function updatePattern(name: string, value: string) {
    setRules(prev => (prev ? { ...prev, patterns: { ...prev.patterns, [name]: value } } : prev));
    setDirty(true);
  }

  function addPattern() {
    setRules(prev => {
      if (!prev) return prev;
      let i = 1;
      let name = `custom_${i}`;
      while (prev.patterns[name]) { i += 1; name = `custom_${i}`; }
      return { ...prev, patterns: { ...prev.patterns, [name]: '' } };
    });
    setDirty(true);
  }

  function removePattern(name: string) {
    setRules(prev => (prev ? { ...prev, patterns: Object.fromEntries(Object.entries(prev.patterns).filter(([k]) => k !== name)) } : prev));
    setDirty(true);
  }

  function updateScoring<K extends keyof Scoring>(key: K, value: Scoring[K]) {
    setRules(prev => (prev ? { ...prev, scoring: { ...prev.scoring, [key]: value } } : prev));
    setDirty(true);
  }

  function updatePenalty(k: keyof LengthPenalty, v: number) {
    setRules(prev => (prev ? { ...prev, scoring: {
      ...prev.scoring,
      length_penalties: { ...prev.scoring.length_penalties, [k]: v },
    } } : prev));
    setDirty(true);
  }

  function updateCountryCode(idx: number, value: string) {
    setRules(prev => {
      if (!prev) return prev;
      const next = prev.country_codes.slice();
      next[idx] = value.toUpperCase();
      return { ...prev, country_codes: next };
    });
    setDirty(true);
  }

  function addCountryCode() {
    setRules(prev => (prev ? { ...prev, country_codes: [...prev.country_codes, ''] } : prev));
    setDirty(true);
  }

  function removeCountryCode(idx: number) {
    setRules(prev => (prev ? { ...prev, country_codes: prev.country_codes.filter((_, i) => i !== idx) } : prev));
    setDirty(true);
  }

  // --- save / reload ---

  async function save() {
    if (!rules) return;
    setError(null);
    // Pre-flight validation so the user gets friendlier errors than the
    // server's 400.
    for (const [name, pat] of Object.entries(rules.patterns)) {
      const err = isValidRegex(pat);
      if (err) { setError(`${t('lpr.invalidPattern')} "${name}": ${err}`); return; }
    }
    for (let i = 0; i < rules.country_codes.length; i += 1) {
      if (!isValidCode(rules.country_codes[i])) {
        setError(t('lpr.invalidCountryCode') + ` (index ${i})`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await api.lprPutRules(rules);
      setRules(res.rules as Rules);
      setMtime(res.mtime);
      setSavedAt(new Date().toISOString());
      setDirty(false);
    } catch (err: any) {
      setError(err.message || t('lpr.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function reload() {
    setReloading(true);
    setError(null);
    try {
      const res = await api.lprReloadRules();
      setMtime(res.mtime);
    } catch (err: any) {
      setError(err.message || t('lpr.reloadFailed'));
    } finally {
      setReloading(false);
    }
  }

  async function fetchPromptAndCopy() {
    setError(null);
    try {
      const res = await api.lprGetLlmPrompt(orgId || undefined);
      await navigator.clipboard.writeText(res.prompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2500);
    } catch (err: any) {
      setError(err.message || t('lpr.promptCopyFailed'));
    }
  }

  async function downloadCsv() {
    setError(null);
    setDownloading(true);
    try {
      // v0.4.9.4.6: was a plain <a href download>, which cannot carry the
      // Bearer token this route requires — it 401'd every time. See
      // api.lprDownloadTrainingCsv.
      const blob = await api.lprDownloadTrainingCsv(undefined, orgId || undefined);
      api.saveBlob(blob, `lpr-training-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err: any) {
      setError(err.message || t('lpr.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  }

  // --- render ---

  if (!rules) {
    return (
      <div className="p-3 sm:p-6 text-sm text-gray-600">
        {error ? (
          <div className="text-red-600">{t('lpr.loadFailed')}: {error}</div>
        ) : (
          t('lpr.loading')
        )}
      </div>
    );
  }

  const canSave = dirty && !saving;
  const dirtyBadge = dirty ? <span className="text-amber-600 ml-2 text-xs">({t('lpr.unsaved')})</span> : null;

  return (
    <div className="p-3 sm:p-6 max-w-4xl space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('lpr.title')}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('lpr.subtitle')}
          {mtime ? ` · ${t('lpr.mtime')}: ${new Date(mtime).toLocaleString()}` : null}
          {dirtyBadge}
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <div>{error}</div>
        </div>
      )}
      {savedAt && !dirty && (
        <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> {t('lpr.savedAt', { at: new Date(savedAt).toLocaleString() })}
        </div>
      )}

      {/* Patterns */}
      <section className="rounded-lg border border-gray-300 bg-white p-3 sm:p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('lpr.patterns')}</h2>
        <p className="text-xs text-gray-600">{t('lpr.patternsHelp')}</p>
        {Object.entries(rules.patterns).map(([name, pat]) => {
          const err = isValidRegex(pat);
          return (
            // v0.4.9.4.6c4: stacks on a phone. This was a single
            // `flex items-start gap-3` line holding a fixed w-32 name, a
            // flex-1 input, an error block up to max-w-xs (320px) and the
            // Remove button. On a 414px screen the fixed parts already
            // consumed ~207px, and an invalid regex made the error block
            // expand — pushing Remove off the right edge entirely. The
            // error text is the thing that grows, so the layout has to give
            // it its own line rather than compete for the same one.
            <div key={name} className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
              <div className="font-mono text-sm sm:w-32 sm:shrink-0 sm:pt-2">{name}</div>
              <input
                type="text"
                value={pat}
                onChange={(e) => updatePattern(name, e.target.value)}
                className={`w-full sm:flex-1 rounded border ${err ? 'border-red-500 bg-red-50' : 'border-gray-300'} bg-white px-2 py-1 font-mono text-sm`}
                spellCheck={false}
                placeholder="^[A-Z0-9]{4,8}$"
              />
              {err && <div className="text-xs text-red-600 sm:max-w-xs sm:pt-2">{err}</div>}
              <button
                onClick={() => removePattern(name)}
                className="self-start text-xs text-red-600 hover:underline sm:pt-2"
                type="button"
              >
                {t('common.remove')}
              </button>
            </div>
          );
        })}
        <button
          onClick={addPattern}
          className="text-sm text-blue-700 hover:underline"
          type="button"
        >
          + {t('lpr.addPattern')}
        </button>
      </section>

      {/* Country codes */}
      <section className="rounded-lg border border-gray-300 bg-white p-3 sm:p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('lpr.countryCodes')}</h2>
        <p className="text-xs text-gray-600">{t('lpr.countryCodesHelp')}</p>
        <div className="flex flex-wrap gap-2">
          {rules.country_codes.map((cc, i) => {
            const valid = isValidCode(cc);
            return (
              <div key={i} className="flex items-center gap-1">
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => updateCountryCode(i, e.target.value)}
                  maxLength={3}
                  className={`w-16 rounded border ${valid ? 'border-gray-300' : 'border-red-500 bg-red-50'} bg-white px-2 py-1 font-mono text-sm uppercase`}
                  spellCheck={false}
                  placeholder="BY"
                />
                <button
                  onClick={() => removeCountryCode(i)}
                  className="text-xs text-red-600 hover:underline"
                  type="button"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
        <button
          onClick={addCountryCode}
          className="text-sm text-blue-700 hover:underline"
          type="button"
        >
          + {t('lpr.addCountryCode')}
        </button>
      </section>

      {/* Scoring */}
      <section className="rounded-lg border border-gray-300 bg-white p-3 sm:p-5 space-y-3">
        <h2 className="text-lg font-semibold">{t('lpr.scoring')}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <div className="text-xs text-gray-600">{t('lpr.minLength')}</div>
            <input
              type="number" min={1} max={20}
              value={rules.scoring.min_length}
              onChange={(e) => updateScoring('min_length', parseInt(e.target.value, 10) || 0)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm">
            <div className="text-xs text-gray-600">{t('lpr.maxLength')}</div>
            <input
              type="number" min={1} max={20}
              value={rules.scoring.max_length}
              onChange={(e) => updateScoring('max_length', parseInt(e.target.value, 10) || 0)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm col-span-2">
            <div className="text-xs text-gray-600">{t('lpr.belarusianWeight')}</div>
            <input
              type="number" step="0.1" min={0.1} max={5}
              value={rules.scoring.belarusian_weight}
              onChange={(e) => updateScoring('belarusian_weight', parseFloat(e.target.value) || 1.0)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            />
          </label>
        </div>
        <div className="mt-2">
          <div className="text-xs text-gray-600 mb-1">{t('lpr.lengthPenalties')}</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {(['lt4', 'lt5', 'gt8', 'gt12', 'gt20'] as (keyof LengthPenalty)[]).map(k => (
              <label key={k} className="text-xs">
                <div className="text-gray-600">{k}</div>
                <input
                  type="number" step="0.05" min={0} max={2}
                  value={rules.scoring.length_penalties[k]}
                  onChange={(e) => updatePenalty(k, parseFloat(e.target.value) || 0)}
                  className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
                />
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <div className="text-xs text-gray-600">{t('lpr.minDigits')}</div>
            <input
              type="number" min={0} max={20}
              value={rules.min_digits}
              onChange={(e) => update('min_digits', parseInt(e.target.value, 10) || 0)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            />
          </label>
          <label className="text-sm">
            <div className="text-xs text-gray-600">{t('lpr.maxDigits')}</div>
            <input
              type="number" min={0} max={20}
              value={rules.max_digits}
              onChange={(e) => update('max_digits', parseInt(e.target.value, 10) || 0)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            />
          </label>
        </div>
      </section>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={save}
          disabled={!canSave}
          className="px-4 py-2 rounded bg-blue-600 text-blue-700-foreground hover:bg-blue-600/90 disabled:opacity-50"
          type="button"
        >
          {saving ? t('lpr.saving') : t('lpr.save')}
        </button>
        <button
          onClick={load}
          disabled={saving || reloading}
          className="px-4 py-2 rounded border border-gray-300 text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          type="button"
        >
          {t('common.reload')}
        </button>
        <button
          onClick={reload}
          disabled={saving || reloading}
          className="w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-900 hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center gap-1"
          type="button"
          title={t('lpr.reloadHelp')}
        >
          <RefreshCw className="w-4 h-4" /> {reloading ? t('lpr.reloading') : t('lpr.forceReload')}
        </button>

        <div className="flex-1" />

        <label className="w-full sm:w-auto flex items-center gap-1 text-xs text-gray-600">
          <span>{t('lpr.orgId')}</span>
          <input
            type="text"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder={t('lpr.orgIdPlaceholder')}
            title={t('lpr.orgIdHelp')}
            className="w-full sm:w-40 px-2 py-1 rounded border border-gray-300 bg-white text-gray-900 font-mono text-xs"
          />
        </label>
        <button
          onClick={downloadCsv}
          disabled={downloading}
          className="w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-900 hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center gap-1"
          type="button"
          title={t('lpr.orgIdHelp')}
        >
          <Download className="w-4 h-4" />
          {downloading ? t('lpr.downloading') : t('lpr.downloadCsv')}
        </button>
        <button
          onClick={fetchPromptAndCopy}
          className="w-full sm:w-auto px-4 py-2 rounded border border-gray-300 text-gray-900 hover:bg-gray-100 flex items-center justify-center gap-1"
          type="button"
        >
          <Sparkles className="w-4 h-4" />
          {copiedPrompt ? t('lpr.copied') : t('lpr.getLlmPrompt')}
        </button>
      </div>

      <details className="text-xs text-gray-600">
        <summary className="cursor-pointer flex items-center gap-1">
          <FileText className="w-3 h-3" /> {t('lpr.viewRaw')}
        </summary>
        <pre className="mt-2 p-2 bg-gray-50 rounded text-[10px] overflow-x-auto max-h-64">
{JSON.stringify(rules, null, 2)}
        </pre>
      </details>
    </div>
  );
}
